"use client";
import { usePathname } from "@/i18n/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Frame = { face: string; ms: number };

interface VariantData {
    rest: string;
    idle: Record<string, Frame[]>;
    click: Record<string, Frame[]>;
    sleep: Frame[];
    wake: Frame[];
    dizzy: Frame[];
    scared: Frame[];
}

const HUMAN: VariantData = {
    rest: "(•‿•)",
    idle: {
        wave: [
            { face: "(•‿•)/", ms: 900 },
            { face: "(•‿•)\\", ms: 220 },
            { face: "(•‿•)/", ms: 220 },
            { face: "(•‿•)\\", ms: 220 },
            { face: "(•‿•)/", ms: 700 },
        ],
        wink: [
            { face: "(•‿•)", ms: 700 },
            { face: "(-‿•)", ms: 260 },
            { face: "(•‿•)", ms: 600 },
        ],
        blink: [
            { face: "(•‿•)", ms: 400 },
            { face: "(-‿-)", ms: 160 },
            { face: "(•‿•)", ms: 400 },
        ],
        yawn: [
            { face: "(•‿•)", ms: 400 },
            { face: "(o‿o)", ms: 350 },
            { face: "(O‿O)", ms: 700 },
            { face: "(o‿o)", ms: 350 },
            { face: "(•‿•)", ms: 400 },
        ],
        stretch: [
            { face: "(•‿•)", ms: 300 },
            { face: "\\(•‿•)/", ms: 900 },
            { face: "(•‿•)", ms: 400 },
        ],
        lookAround: [
            { face: "(•‿• )", ms: 500 },
            { face: "( •‿•)", ms: 500 },
            { face: "(•‿•)", ms: 500 },
        ],
        thinking: [
            { face: "(•_•)", ms: 500 },
            { face: "(•_•)?", ms: 1100 },
            { face: "(•‿•)", ms: 400 },
        ],
        shrug: [
            { face: "(•‿•)", ms: 250 },
            { face: "¯\\(•‿•)/¯", ms: 1200 },
            { face: "(•‿•)", ms: 300 },
        ],
        whistle: [
            { face: "(•‿•)", ms: 250 },
            { face: "(•⌣•)", ms: 600 },
            { face: "(•o•)~", ms: 800 },
        ],
    },
    click: {
        sunglasses: [
            { face: "(•‿•)", ms: 120 },
            { face: "(⌐■_■)", ms: 2400 },
        ],
        heart: [
            { face: "(•‿•)", ms: 120 },
            { face: "(♥‿♥)", ms: 2200 },
        ],
        surprised: [
            { face: "(⊙_⊙)", ms: 1800 },
            { face: "(•‿•)", ms: 350 },
        ],
        tongue: [
            { face: "(•‿•)", ms: 120 },
            { face: "(•‿•)P", ms: 2000 },
        ],
        star: [{ face: "(*‿*)", ms: 2400 }],
        dance: [
            { face: "\\(•‿•)/", ms: 220 },
            { face: "/(•‿•)\\", ms: 220 },
            { face: "\\(•‿•)/", ms: 220 },
            { face: "/(•‿•)\\", ms: 220 },
            { face: "\\(•‿•)/", ms: 220 },
            { face: "/(•‿•)\\", ms: 220 },
            { face: "\\(•‿•)/", ms: 220 },
            { face: "/(•‿•)\\", ms: 220 },
        ],
        flex: [
            { face: "(•‿•)", ms: 200 },
            { face: "ᕦ(•‿•)ᕤ", ms: 2200 },
        ],
        cool: [
            { face: "(•‿•)", ms: 150 },
            { face: "(•‿•)b", ms: 2000 },
        ],
        coffee: [
            { face: "(•‿•)", ms: 200 },
            { face: "(•‿•)U", ms: 800 },
            { face: "(º‿º)U", ms: 1100 },
            { face: "(•‿•)", ms: 600 },
        ],
        monocle: [
            { face: "(•‿•)", ms: 150 },
            { face: "(◉‿•)", ms: 2100 },
        ],
        evil: [
            { face: "(•‿•)", ms: 150 },
            { face: "(¬‿¬)", ms: 2200 },
        ],
        cry: [
            { face: "(•‿•)", ms: 150 },
            { face: "(╥﹏╥)", ms: 2000 },
        ],
        party: [
            { face: "\\(◕‿◕)/", ms: 280 },
            { face: "♪\\(◕‿◕)/♪", ms: 380 },
            { face: "\\(◕‿◕)/", ms: 280 },
            { face: "♪\\(◕‿◕)/♪", ms: 380 },
            { face: "\\(◕‿◕)/", ms: 280 },
            { face: "♪\\(◕‿◕)/♪", ms: 380 },
        ],
        mindblown: [
            { face: "(•‿•)", ms: 150 },
            { face: "(⊙_⊙)", ms: 400 },
            { face: "(O_O)", ms: 1800 },
        ],
        ninja: [
            { face: "(•‿•)", ms: 150 },
            { face: "(•‿•)シ", ms: 350 },
            { face: "シ", ms: 1200 },
            { face: "(•‿•)", ms: 350 },
        ],
        kiss: [
            { face: "(•‿•)", ms: 150 },
            { face: "(•3•)", ms: 700 },
            { face: "(•3•)~♥", ms: 1500 },
        ],
        music: [
            { face: "(•‿•)", ms: 250 },
            { face: "(•‿•)♪", ms: 600 },
            { face: "(•‿•)♫", ms: 600 },
            { face: "(•‿•)♪", ms: 600 },
        ],
    },
    sleep: [
        { face: "(-_-) z", ms: 900 },
        { face: "(-_-) zZ", ms: 900 },
        { face: "(-_-) Zz", ms: 900 },
    ],
    wake: [
        { face: "(⊙_⊙)", ms: 350 },
        { face: "(•‿•)", ms: 400 },
    ],
    dizzy: [
        { face: "(@_@)", ms: 130 },
        { face: "(x_o)", ms: 130 },
        { face: "(o_x)", ms: 130 },
        { face: "(@_@)", ms: 130 },
        { face: "(x_o)", ms: 130 },
        { face: "(o_x)", ms: 130 },
        { face: "(@_@)", ms: 130 },
        { face: "(x_o)", ms: 130 },
        { face: "(•_•)…", ms: 800 },
        { face: "(•‿•)", ms: 400 },
    ],
    scared: [
        { face: "(°□°)", ms: 220 },
        { face: "(°□°)!", ms: 280 },
        { face: "(°□°)!!", ms: 1100 },
        { face: "(°□°)", ms: 500 },
        { face: "(•_•)…", ms: 900 },
        { face: "(•‿•)", ms: 400 },
    ],
};

const DOG: VariantData = {
    rest: "(•ᴥ•)",
    idle: {
        wagTail: [
            { face: "(•ᴥ•)", ms: 250 },
            { face: "(•ᴥ•)~", ms: 220 },
            { face: "(•ᴥ•)~~", ms: 220 },
            { face: "(•ᴥ•)~", ms: 220 },
            { face: "(•ᴥ•)", ms: 250 },
        ],
        pant: [
            { face: "(•ᴥ•)", ms: 350 },
            { face: "(•ᴥ•)P", ms: 350 },
            { face: "(•ᴥ•)", ms: 350 },
            { face: "(•ᴥ•)P", ms: 350 },
        ],
        sniff: [
            { face: "(•ᴥ•)", ms: 350 },
            { face: "(>ᴥ<)", ms: 700 },
            { face: "(•ᴥ•)", ms: 350 },
        ],
        blink: [
            { face: "(•ᴥ•)", ms: 400 },
            { face: "(-ᴥ-)", ms: 160 },
            { face: "(•ᴥ•)", ms: 400 },
        ],
        headTilt: [
            { face: "(•ᴥ•)", ms: 350 },
            { face: "(°ᴥ°)?", ms: 1100 },
            { face: "(•ᴥ•)", ms: 350 },
        ],
        sit: [
            { face: "(•ᴥ•)", ms: 350 },
            { face: "(_ᴥ_)", ms: 1100 },
            { face: "(•ᴥ•)", ms: 350 },
        ],
    },
    click: {
        bark: [
            { face: "(•ᴥ•)", ms: 120 },
            { face: "(•ᴥ•)!", ms: 320 },
            { face: "(•ᴥ•)!!", ms: 1500 },
            { face: "(•ᴥ•)", ms: 350 },
        ],
        lick: [
            { face: "(•ᴥ•)", ms: 150 },
            { face: "(•ᴥ•)d", ms: 1700 },
        ],
        paw: [
            { face: "(•ᴥ•)", ms: 200 },
            { face: "(•ᴥ•)/\\", ms: 1700 },
        ],
        jump: [
            { face: "(•ᴥ•)", ms: 150 },
            { face: "\\(•ᴥ•)/", ms: 600 },
            { face: "(•ᴥ•)", ms: 200 },
            { face: "\\(•ᴥ•)/", ms: 800 },
        ],
        treat: [
            { face: "(•ᴥ•)", ms: 150 },
            { face: "(◕ᴥ◕)", ms: 2200 },
        ],
        zoomies: [
            { face: "\\(•ᴥ•)/", ms: 200 },
            { face: "/(•ᴥ•)\\", ms: 200 },
            { face: "\\(•ᴥ•)/", ms: 200 },
            { face: "/(•ᴥ•)\\", ms: 200 },
            { face: "\\(•ᴥ•)/", ms: 200 },
            { face: "/(•ᴥ•)\\", ms: 200 },
            { face: "\\(•ᴥ•)/", ms: 200 },
            { face: "/(•ᴥ•)\\", ms: 200 },
        ],
        fetch: [
            { face: "(•ᴥ•)", ms: 200 },
            { face: "(•ᴥ•)~o", ms: 600 },
            { face: "(•ᴥ•)o", ms: 1500 },
        ],
        spin: [
            { face: "(>ᴥ<)", ms: 250 },
            { face: "(<ᴥ>)", ms: 250 },
            { face: "(>ᴥ<)", ms: 250 },
            { face: "(<ᴥ>)", ms: 250 },
            { face: "(•ᴥ•)", ms: 600 },
        ],
        bellyRub: [
            { face: "(•ᴥ•)", ms: 200 },
            { face: "(♥ᴥ♥)", ms: 2200 },
        ],
    },
    sleep: [
        { face: "(=ᴥ=) z", ms: 900 },
        { face: "(=ᴥ=) zZ", ms: 900 },
        { face: "(=ᴥ=) Zz", ms: 900 },
    ],
    wake: [
        { face: "(OᴥO)", ms: 350 },
        { face: "(•ᴥ•)", ms: 400 },
    ],
    dizzy: [
        { face: "(@ᴥ@)", ms: 130 },
        { face: "(xᴥo)", ms: 130 },
        { face: "(oᴥx)", ms: 130 },
        { face: "(@ᴥ@)", ms: 130 },
        { face: "(xᴥo)", ms: 130 },
        { face: "(oᴥx)", ms: 130 },
        { face: "(@ᴥ@)", ms: 130 },
        { face: "(>ᴥ<)…", ms: 800 },
        { face: "(•ᴥ•)", ms: 400 },
    ],
    scared: [
        { face: "(>ᴥ<)", ms: 200 },
        { face: "(>ᴥ<)!", ms: 280 },
        { face: "(OᴥO)!!", ms: 1100 },
        { face: "(>ᴥ<)…", ms: 900 },
        { face: "(•ᴥ•)", ms: 400 },
    ],
};

const STONE: VariantData = {
    rest: "◢•_•◣",
    idle: {
        stare: [{ face: "◢•_•◣", ms: 1500 }],
        blink: [
            { face: "◢•_•◣", ms: 500 },
            { face: "◢-_-◣", ms: 200 },
            { face: "◢•_•◣", ms: 500 },
        ],
        sigh: [
            { face: "◢•_•◣", ms: 350 },
            { face: "◢•_•◣…", ms: 1300 },
            { face: "◢•_•◣", ms: 350 },
        ],
        faintSmile: [
            { face: "◢•_•◣", ms: 350 },
            { face: "◢•‿•◣", ms: 900 },
            { face: "◢•_•◣", ms: 350 },
        ],
        creak: [
            { face: "◢•_•◣", ms: 350 },
            { face: "◢ •_• ◣", ms: 600 },
            { face: "◢•_•◣", ms: 350 },
        ],
    },
    click: {
        startle: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢O_O◣", ms: 1800 },
        ],
        annoyed: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢¬_¬◣", ms: 2000 },
        ],
        smile: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢•‿•◣", ms: 2000 },
        ],
        sparkle: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢*_*◣", ms: 2000 },
        ],
        chip: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢•_• ", ms: 250 },
            { face: "◢•_•  ", ms: 1700 },
            { face: "◢•_•◣", ms: 250 },
        ],
        snore: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢-_-◣z", ms: 1900 },
        ],
        glare: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢ò_ó◣", ms: 2000 },
        ],
        crack: [
            { face: "◢•_•◣", ms: 150 },
            { face: "◢•/_•◣", ms: 1900 },
        ],
    },
    sleep: [
        { face: "◢—_—◣ z", ms: 1000 },
        { face: "◢—_—◣ zZ", ms: 1000 },
        { face: "◢—_—◣ Zz", ms: 1000 },
    ],
    wake: [
        { face: "◢O_O◣", ms: 400 },
        { face: "◢•_•◣", ms: 500 },
    ],
    dizzy: [
        { face: "◢@_@◣", ms: 200 },
        { face: "◢x_o◣", ms: 200 },
        { face: "◢o_x◣", ms: 200 },
        { face: "◢@_@◣", ms: 200 },
        { face: "◢x_o◣", ms: 200 },
        { face: "◢o_x◣", ms: 200 },
        { face: "◢•_•◣…", ms: 1000 },
        { face: "◢•_•◣", ms: 500 },
    ],
    scared: [
        { face: "◢O_O◣", ms: 280 },
        { face: "◢O_O◣!", ms: 1100 },
        { face: "◢•_•◣…", ms: 900 },
        { face: "◢•_•◣", ms: 500 },
    ],
};

const COFFEE: VariantData = {
    rest: "c[•‿•]",
    idle: {
        steam: [
            { face: "c[•‿•]", ms: 350 },
            { face: "c[•‿•]~", ms: 280 },
            { face: "c[•‿•]~~", ms: 280 },
            { face: "c[•‿•]~", ms: 280 },
        ],
        sip: [
            { face: "c[•‿•]", ms: 350 },
            { face: "c[•u•]", ms: 800 },
            { face: "c[•‿•]", ms: 350 },
        ],
        blink: [
            { face: "c[•‿•]", ms: 400 },
            { face: "c[-‿-]", ms: 160 },
            { face: "c[•‿•]", ms: 400 },
        ],
        jitter: [
            { face: "c[•‿•]", ms: 250 },
            { face: "c[••‿••]", ms: 600 },
            { face: "c[•‿•]", ms: 250 },
        ],
        look: [
            { face: "c[•‿• ]", ms: 450 },
            { face: "c[ •‿•]", ms: 450 },
            { face: "c[•‿•]", ms: 400 },
        ],
        cheer: [
            { face: "c[•‿•]", ms: 250 },
            { face: "c[◕‿◕]", ms: 800 },
            { face: "c[•‿•]", ms: 350 },
        ],
    },
    click: {
        espresso: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[*‿*]", ms: 2200 },
        ],
        latte: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[♥‿♥]", ms: 2100 },
        ],
        foam: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[●‿●]", ms: 2000 },
        ],
        empty: [
            { face: "c[•‿•]", ms: 200 },
            { face: "c[ _ ]", ms: 1700 },
            { face: "c[•_•]…", ms: 600 },
        ],
        cool: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[⌐■_■]", ms: 2200 },
        ],
        bean: [
            { face: "c[•‿•]", ms: 200 },
            { face: "c[•‿•]b", ms: 2000 },
        ],
        tasting: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[•◡•]", ms: 2200 },
        ],
        burnt: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[>_<]!", ms: 1800 },
            { face: "c[•_•]", ms: 350 },
        ],
        refill: [
            { face: "c[•‿•]", ms: 150 },
            { face: "c[•‿•]++", ms: 2000 },
        ],
    },
    sleep: [
        { face: "c[—_—] z", ms: 900 },
        { face: "c[—_—] zZ", ms: 900 },
        { face: "c[—_—] Zz", ms: 900 },
    ],
    wake: [
        { face: "c[O_O]", ms: 350 },
        { face: "c[•‿•]", ms: 400 },
    ],
    dizzy: [
        { face: "c[@_@]", ms: 130 },
        { face: "c[x_o]", ms: 130 },
        { face: "c[o_x]", ms: 130 },
        { face: "c[@_@]", ms: 130 },
        { face: "c[x_o]", ms: 130 },
        { face: "c[o_x]", ms: 130 },
        { face: "c[•_•]…", ms: 800 },
        { face: "c[•‿•]", ms: 400 },
    ],
    scared: [
        { face: "c[O_O]", ms: 250 },
        { face: "c[O_O]!", ms: 1100 },
        { face: "c[•_•]…", ms: 900 },
        { face: "c[•‿•]", ms: 400 },
    ],
};

const TRAILS: VariantData = {
    rest: "△ ʕ•‿•ʔ ※",
    idle: {
        fire: [
            { face: "△ ʕ•‿•ʔ ※", ms: 350 },
            { face: "△ ʕ•‿•ʔ ✺", ms: 350 },
            { face: "△ ʕ•‿•ʔ ✻", ms: 350 },
            { face: "△ ʕ•‿•ʔ ※", ms: 350 },
        ],
        breathe: [
            { face: "△ ʕ•‿•ʔ ※", ms: 400 },
            { face: "△ ʕO‿Oʔ ※", ms: 700 },
            { face: "△ ʕ•‿•ʔ ※", ms: 400 },
        ],
        blink: [
            { face: "△ ʕ•‿•ʔ ※", ms: 450 },
            { face: "△ ʕ-‿-ʔ ※", ms: 180 },
            { face: "△ ʕ•‿•ʔ ※", ms: 450 },
        ],
        lookAround: [
            { face: "△ ʕ•‿• ʔ ※", ms: 550 },
            { face: "△ ʕ •‿•ʔ ※", ms: 550 },
            { face: "△ ʕ•‿•ʔ ※", ms: 400 },
        ],
        warmHands: [
            { face: "△ ʕ•‿•ʔ ※", ms: 350 },
            { face: "△ ʕっ‿っʔ ※", ms: 1300 },
            { face: "△ ʕ•‿•ʔ ※", ms: 350 },
        ],
    },
    click: {
        smore: [
            { face: "△ ʕ•‿•ʔ ※", ms: 200 },
            { face: "△ ʕ◕‿◕ʔ ※", ms: 2200 },
        ],
        guitar: [
            { face: "△ ʕ•‿•ʔ ※", ms: 200 },
            { face: "△ ʕ•‿•ʔ ♪", ms: 600 },
            { face: "△ ʕ•‿•ʔ ♫", ms: 600 },
            { face: "△ ʕ•‿•ʔ ♪", ms: 600 },
        ],
        starGaze: [
            { face: "△ ʕ•‿•ʔ ※", ms: 200 },
            { face: "△ ʕ*‿*ʔ ✦", ms: 2300 },
        ],
        howl: [
            { face: "△ ʕ•‿•ʔ ※", ms: 200 },
            { face: "△ ʕ•O•ʔ ※", ms: 1900 },
        ],
    },
    sleep: [
        { face: "△ ʕ-_-ʔ ⌇", ms: 1000 },
        { face: "△ ʕ-_-ʔ z", ms: 1000 },
        { face: "△ ʕ-_-ʔ zZ", ms: 1000 },
    ],
    wake: [
        { face: "△ ʕO_Oʔ ※", ms: 350 },
        { face: "△ ʕ•‿•ʔ ※", ms: 400 },
    ],
    dizzy: [
        { face: "△ ʕ@_@ʔ ※", ms: 130 },
        { face: "△ ʕx_oʔ ※", ms: 130 },
        { face: "△ ʕo_xʔ ※", ms: 130 },
        { face: "△ ʕ@_@ʔ ※", ms: 130 },
        { face: "△ ʕx_oʔ ※", ms: 130 },
        { face: "△ ʕ•_•ʔ…", ms: 800 },
        { face: "△ ʕ•‿•ʔ ※", ms: 400 },
    ],
    scared: [
        { face: "△ ʕO_Oʔ ※", ms: 250 },
        { face: "△ ʕO_Oʔ! ※", ms: 1100 },
        { face: "△ ʕ•_•ʔ…", ms: 900 },
        { face: "△ ʕ•‿•ʔ ※", ms: 400 },
    ],
};

const VARIANTS: Record<string, VariantData> = {
    human: HUMAN,
    dog: DOG,
    stone: STONE,
    coffee: COFFEE,
    trails: TRAILS,
};

function detectVariant(pathname: string | null): string {
    if (!pathname) return "human";
    if (pathname.startsWith("/dog")) return "dog";
    if (pathname.startsWith("/labs/stone") || pathname.startsWith("/stone")) {
        return "stone";
    }
    if (pathname.startsWith("/coffee")) return "coffee";
    if (pathname.startsWith("/trails")) return "trails";
    return "human";
}

const SLEEP_AFTER_MS = 30000;
const SPAM_WINDOW_MS = 1500;
const SPAM_THRESHOLD = 4;
const REST_IDLE_MIN = 2800;
const REST_IDLE_VAR = 1800;
const REST_CLICK_MIN = 4500;
const REST_CLICK_VAR = 2500;

export default function AsciiPulse({
    variant: variantProp,
}: {
    variant?: keyof typeof VARIANTS;
}) {
    const pathname = usePathname();
    const variant = variantProp ?? detectVariant(pathname);
    const data = VARIANTS[variant] ?? HUMAN;

    const idleKeys = useMemo(() => Object.keys(data.idle), [data]);
    const clickKeys = useMemo(() => Object.keys(data.click), [data]);

    const [animKey, setAnimKey] = useState<string>("rest");
    const [frameIdx, setFrameIdx] = useState(0);
    const [restMs, setRestMs] = useState(REST_IDLE_MIN);
    const [reduceMotion, setReduceMotion] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setReduceMotion(mq.matches);
        onChange();
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    const clickCountRef = useRef(0);
    const clickTimesRef = useRef<number[]>([]);
    const lastInteractRef = useRef<number>(0);
    useEffect(() => {
        if (lastInteractRef.current === 0) lastInteractRef.current = Date.now();
    }, []);

    const currentAnim: Frame[] = useMemo(() => {
        if (animKey === "rest") return [{ face: data.rest, ms: restMs }];
        if (animKey === "sleep") return data.sleep;
        if (animKey === "wake") return data.wake;
        if (animKey === "dizzy") return data.dizzy;
        if (animKey === "scared") return data.scared;
        return data.idle[animKey] ?? data.click[animKey] ?? [{ face: data.rest, ms: 1000 }];
    }, [animKey, restMs, data]);

    const frame = currentAnim[Math.min(frameIdx, currentAnim.length - 1)];

    useEffect(() => {
        if (reduceMotion && animKey === "rest") return;

        const t = setTimeout(() => {
            const next = frameIdx + 1;
            if (next < currentAnim.length) {
                setFrameIdx(next);
                return;
            }

            if (animKey === "sleep") {
                setFrameIdx(0);
                return;
            }

            const idleFor = Date.now() - lastInteractRef.current;
            if (idleFor > SLEEP_AFTER_MS) {
                setAnimKey("sleep");
                setFrameIdx(0);
                return;
            }

            if (animKey === "rest") {
                if (reduceMotion) return;
                const nextKey =
                    idleKeys[Math.floor(Math.random() * idleKeys.length)];
                setAnimKey(nextKey);
                setFrameIdx(0);
                return;
            }

            const wasClickReact =
                clickKeys.includes(animKey) ||
                animKey === "dizzy" ||
                animKey === "scared";
            const ms = wasClickReact
                ? REST_CLICK_MIN + Math.random() * REST_CLICK_VAR
                : REST_IDLE_MIN + Math.random() * REST_IDLE_VAR;
            setRestMs(ms);
            setAnimKey("rest");
            setFrameIdx(0);
        }, frame.ms);
        return () => clearTimeout(t);
    }, [animKey, frameIdx, frame.ms, currentAnim, idleKeys, clickKeys, reduceMotion]);

    const btnRef = useRef<HTMLButtonElement>(null);

    const triggerScare = useCallback(() => {
        if (animKey === "dizzy" || animKey === "scared") return;
        lastInteractRef.current = Date.now();
        setAnimKey("scared");
        setFrameIdx(0);
    }, [animKey]);

    const onClick = useCallback(() => {
        const now = Date.now();
        lastInteractRef.current = now;

        if (animKey === "sleep") {
            clickTimesRef.current = [];
            clickCountRef.current = 0;
            setAnimKey("scared");
            setFrameIdx(0);
            return;
        }

        clickTimesRef.current = clickTimesRef.current.filter(
            (t) => now - t < SPAM_WINDOW_MS,
        );
        clickTimesRef.current.push(now);

        if (clickTimesRef.current.length >= SPAM_THRESHOLD) {
            clickTimesRef.current = [];
            clickCountRef.current = 0;
            setAnimKey("dizzy");
            setFrameIdx(0);
            return;
        }

        if (animKey === "dizzy" || animKey === "scared") return;

        const idx = clickCountRef.current % clickKeys.length;
        clickCountRef.current += 1;
        setAnimKey(clickKeys[idx]);
        setFrameIdx(0);
    }, [animKey, clickKeys]);

    const onContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            triggerScare();
        },
        [triggerScare],
    );

    useEffect(() => {
        const btn = btnRef.current;
        if (!btn) return;
        let lastX = 0;
        let lastY = 0;
        let lastT = 0;
        let lastScareAt = 0;

        const onMove = (e: MouseEvent) => {
            const now = performance.now();
            if (lastT > 0) {
                const dt = now - lastT;
                if (dt > 0 && dt < 100) {
                    const dx = e.clientX - lastX;
                    const dy = e.clientY - lastY;
                    const speed = Math.hypot(dx, dy) / dt;
                    const r = btn.getBoundingClientRect();
                    const cx = r.left + r.width / 2;
                    const cy = r.top + r.height / 2;
                    const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
                    const towards =
                        dist > 0
                            ? ((cx - e.clientX) * dx + (cy - e.clientY) * dy) / dist
                            : 0;
                    if (
                        speed > 2.4 &&
                        dist < 110 &&
                        towards > 1 &&
                        now - lastScareAt > 6000
                    ) {
                        lastScareAt = now;
                        triggerScare();
                    }
                }
            }
            lastX = e.clientX;
            lastY = e.clientY;
            lastT = now;
        };

        window.addEventListener("mousemove", onMove, { passive: true });
        return () => window.removeEventListener("mousemove", onMove);
    }, [triggerScare]);

    useEffect(() => {
        const obs = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.attributeName === "data-theme") {
                    lastInteractRef.current = Date.now();
                    if (animKey === "dizzy") return;
                    setAnimKey("wake");
                    setFrameIdx(0);
                    break;
                }
            }
        });
        obs.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme"],
        });
        return () => obs.disconnect();
    }, [animKey]);

    useEffect(() => {
        const onVis = () => {
            if (document.hidden) {
                lastInteractRef.current = Date.now() - SLEEP_AFTER_MS - 1;
            } else {
                lastInteractRef.current = Date.now();
                if (animKey === "sleep") {
                    setAnimKey("wake");
                    setFrameIdx(0);
                }
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [animKey]);

    const isDizzy = animKey === "dizzy";

    return (
        <button
            ref={btnRef}
            type="button"
            onClick={onClick}
            onContextMenu={onContextMenu}
            aria-label="mascot · haz click"
            title="haz click"
            className="select-none cursor-pointer font-mono text-base text-muted-foreground sm:text-lg"
        >
            <span className={isDizzy ? "mascot-dizzy" : undefined}>{frame.face}</span>
        </button>
    );
}
